import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowLeft, ArrowRight, Mail, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"request" | "reset" | "success">("request");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  // Detect if user arrived via a reset link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const tokenEmail = params.get("email");

    if (token && tokenEmail) {
      // User clicked the reset link in email — verify the token to create a session
      setLoading(true);
      supabase.auth.verifyOtp({
        token_hash: token,
        type: "recovery",
      }).then(({ error }) => {
        if (error) {
          setError("This reset link has expired or is invalid. Please request a new one.");
        } else {
          setMode("reset");
        }
        setLoading(false);
      });
      // Clean the URL so token isn't visible
      window.history.replaceState({}, "", "/reset-password");
      return;
    }

    // Also handle Supabase hash-based redirects (fallback)
    const hash = window.location.hash;
    if (hash && hash.includes("type=recovery")) {
      setMode("reset");
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setMode("reset");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Use our edge function which sends via Resend (branded, fast)
      // instead of Supabase's built-in email which goes through /auth/v1/verify (slow)
      const { data, error } = await supabase.functions.invoke("send-password-reset", {
        body: { email },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setEmailSent(true);
    } catch (err: any) {
      // Don't reveal if email exists or not — always show success
      setEmailSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setMode("success");
      // Sign out so they log in fresh with new password
      await supabase.auth.signOut();
    } catch (err: any) {
      setError(err.message || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground">
            {mode === "request" ? "Reset Password" : mode === "reset" ? "Set New Password" : "Password Updated"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {mode === "request"
              ? "Enter your email and we'll send you a reset link"
              : mode === "reset"
                ? "Choose a new password for your account"
                : "You can now sign in with your new password"}
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6">
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive mb-4">
              {error}
            </div>
          )}

          {mode === "request" && !emailSent && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-surface border border-border rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>
              <Button type="submit" variant="gradient" size="xl" className="w-full" disabled={loading}>
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          )}

          {mode === "request" && emailSent && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-foreground font-medium">Check your email</p>
              <p className="text-sm text-muted-foreground mt-2">
                We sent a password reset link to <strong>{email}</strong>. Click the link in the email to set a new password.
              </p>
            </div>
          )}

          {mode === "reset" && (
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-surface border border-border rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-surface border border-border rounded-lg text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              <Button type="submit" variant="gradient" size="xl" className="w-full" disabled={loading}>
                {loading ? "Updating..." : "Set New Password"}
              </Button>
            </form>
          )}

          {mode === "success" && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-foreground font-medium">Password updated successfully</p>
              <Button variant="gradient" className="mt-4" onClick={() => navigate("/login")}>
                <ArrowRight className="w-4 h-4 mr-2" />
                Go to Login
              </Button>
            </div>
          )}

          {mode !== "success" && (
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Login
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
